import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Trophy, Target, Calendar, Radio } from 'lucide-react';

export const FriendlyMatchRadar = () => {
  const navigate = useNavigate();
  const API_URL = import.meta.env.VITE_API_URL || 'https://techtennis-api.vercel.app';
  const getAuthHeader = () => {
    const t = localStorage.getItem('tt_auth_token');
    return t ? { 'Authorization': `Bearer ${t}` } : {} as HeadersInit;
  };
  
  const [radius, setRadius] = useState<number>(() => Number(localStorage.getItem('tt_fm_radius')) || 10);
  const [myCategory, setMyCategory] = useState<string>(() => localStorage.getItem('tt_fm_mycat') || '');
  
  const opponentCategories = ['Especial', 'A', 'B', 'C'];
  const [selectedOpponentCategories, setSelectedOpponentCategories] = useState<string[]>(() => {
    const saved = localStorage.getItem('tt_fm_oppcats');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [courtType, setCourtType] = useState<'Possui Quadra' | 'Quadra Alugada' | ''>(() => localStorage.getItem('tt_fm_court') as any || '');
  
  const ageOptions = ['Até 25 anos', '25 a 35 anos', '35 a 50 anos', '50 a 60 anos', 'Acima de 60 anos'];
  
  const [myAge, setMyAge] = useState<string>(() => localStorage.getItem('tt_fm_myage') || '');
  const [selectedAges, setSelectedAges] = useState<string[]>(() => {
    const saved = localStorage.getItem('tt_fm_ages');
    return saved ? JSON.parse(saved) : [];
  });

  const [isActive, setIsActive] = useState(() => localStorage.getItem('tt_radar_amistoso_active') === 'true');
  const [matchFound, setMatchFound] = useState<any>(() => {
    const saved = localStorage.getItem('tt_radar_amistoso_match');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [previousChats, setPreviousChats] = useState<any[]>([]);

  const fetchPreviousChats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/friendly-match/chats`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        setPreviousChats(await res.json());
      }
    } catch (e) {
      console.error('Erro ao carregar conversas anteriores:', e);
    }
  };

  useEffect(() => {
    fetchPreviousChats();
    const interval = setInterval(fetchPreviousChats, 10000);
    return () => clearInterval(interval);
  }, []);

  // Keep localStorage in sync with state changes
  useEffect(() => { localStorage.setItem('tt_fm_radius', radius.toString()); }, [radius]);
  useEffect(() => { localStorage.setItem('tt_fm_mycat', myCategory); }, [myCategory]);
  useEffect(() => { localStorage.setItem('tt_fm_oppcats', JSON.stringify(selectedOpponentCategories)); }, [selectedOpponentCategories]);
  useEffect(() => { localStorage.setItem('tt_fm_court', courtType); }, [courtType]);
  useEffect(() => { localStorage.setItem('tt_fm_myage', myAge); }, [myAge]);
  useEffect(() => { localStorage.setItem('tt_fm_ages', JSON.stringify(selectedAges)); }, [selectedAges]);

  // Listen to cross-tab or timer match events
  useEffect(() => {
    const interval = setInterval(() => {
      const match = localStorage.getItem('tt_radar_amistoso_match');
      if (match) {
        setMatchFound(JSON.parse(match));
      } else {
        setMatchFound(null);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const toggleOpponentCategory = (cat: string) => {
    setSelectedOpponentCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const handleToggleAllOpponents = () => {
    if (selectedOpponentCategories.length === opponentCategories.length) {
      setSelectedOpponentCategories([]);
    } else {
      setSelectedOpponentCategories([...opponentCategories]);
    }
  };

  const toggleAge = (age: string) => {
    setSelectedAges(prev => 
      prev.includes(age) ? prev.filter(a => a !== age) : [...prev, age]
    );
  };

  const handleToggleAllAges = () => {
    if (selectedAges.length === ageOptions.length) {
      setSelectedAges([]);
    } else {
      setSelectedAges([...ageOptions]);
    }
  };

  const requestLocation = () => {
    if (!navigator.geolocation) {
      alert('Seu navegador não suporta geolocalização.');
      return false;
    }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          resolve(loc);
        },
        () => {
          alert('Por favor, permita o acesso à sua localização para buscar amistosos.');
          resolve(false);
        }
      );
    });
  };

  const handleActivateRadar = async () => {
    if (!myCategory) {
      alert('Por favor, selecione sua categoria.');
      return;
    }
    if (selectedOpponentCategories.length === 0) {
      alert('Selecione pelo menos uma categoria de adversário.');
      return;
    }
    if (!courtType) {
      alert('Informe sobre a quadra.');
      return;
    }
    if (!myAge) {
      alert('Por favor, informe a sua idade.');
      return;
    }
    
    const loc: any = await requestLocation();
    if (!loc) return;

    try {
      const res = await fetch(`${API_URL}/api/friendly-match/profile`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          myCategory,
          opponentCategories: selectedOpponentCategories,
          court: courtType,
          age: JSON.stringify(selectedAges),
          myAge,
          radius,
          isOnline: true,
          lat: loc.lat,
          lng: loc.lng
        })
      });
      if (res.ok) {
        setIsActive(true);
        localStorage.setItem('tt_radar_amistoso_active', 'true');
        localStorage.setItem('tt_radar_amistoso_time', Date.now().toString());
        alert('Você agora está ativo no Radar de Busca para amistosos! Você pode navegar por outras telas enquanto busca.');
      } else {
        alert('Erro ao ativar radar.');
      }
    } catch(e) {
      alert('Erro de conexão.');
    }
  };

  const handleDeactivateRadar = async () => {
    if (matchFound?.userId) {
      try {
        await fetch(`${API_URL}/api/friendly-match/chat/${matchFound.userId}`, {
          method: 'POST',
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: 'Match Descartado' })
        });
        fetchPreviousChats();
      } catch (e) {
        console.error('Erro ao registrar descarte de match:', e);
      }
    }
    setIsActive(false);
    setMatchFound(null);
    setIsChatOpen(false);
    localStorage.removeItem('tt_radar_amistoso_active');
    localStorage.removeItem('tt_radar_amistoso_time');
    localStorage.removeItem('tt_radar_amistoso_match');
    try {
      await fetch(`${API_URL}/api/friendly-match/profile`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ isOnline: false })
      });
    } catch(e) {}
  };

  const getUserIdFromToken = () => {
    const token = localStorage.getItem('tt_auth_token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId;
    } catch(e) { return null; }
  };

  const fetchMessages = async () => {
    if (!matchFound?.userId) return;
    try {
      const res = await fetch(`${API_URL}/api/friendly-match/chat/${matchFound.userId}`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        const myUserId = getUserIdFromToken();
        const formattedMessages = data.map((m: any) => ({
          text: m.text,
          sender: m.senderId === myUserId ? 'me' : 'opponent',
          time: new Date(m.createdAt)
        }));
        setChatMessages(formattedMessages);
      }
    } catch(e) {}
  };

  useEffect(() => {
    let interval: any;
    if (isChatOpen && matchFound) {
      fetchMessages();
      interval = setInterval(fetchMessages, 3000);
    }
    return () => clearInterval(interval);
  }, [isChatOpen, matchFound]);

  const handleSendMessage = async () => {
    if (!chatMessage.trim() || !matchFound?.userId) return;
    const msgText = chatMessage;
    setChatMessage('');
    setChatMessages(prev => [...prev, { text: msgText, sender: 'me', time: new Date() }]);
    
    try {
      await fetch(`${API_URL}/api/friendly-match/chat/${matchFound.userId}`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: msgText })
      });
      fetchMessages();
    } catch(e) {}
  };

  return (
    <div style={{ minHeight: '100vh', padding: '120px 5% 40px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <button onClick={() => navigate('/')} style={{ background: 'var(--bg-panel)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ fontSize: '32px', fontWeight: 800, color: 'var(--text-primary)', margin: 0, fontFamily: 'var(--font-heading)' }}>
          Buscar Amistoso
        </h1>
      </div>

      <div className="glass-panel" style={{ padding: '32px', borderRadius: '24px' }}>
        {isChatOpen && matchFound ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '60vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '16px', marginBottom: '16px' }}>
              <button onClick={() => setIsChatOpen(false)} style={{ background: 'var(--bg-panel)', border: 'none', width: '40px', height: '40px', borderRadius: '50%', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <ArrowLeft size={20} />
              </button>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'var(--primary-color)', color: 'var(--text-dark)', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 800, fontSize: '20px' }}>
                {matchFound.name ? matchFound.name.substring(0, 2).toUpperCase() : 'AD'}
              </div>
              <div>
                <h3 style={{ color: 'white', margin: '0 0 4px 0' }}>{matchFound.name}</h3>
                <div style={{ color: '#10B981', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '8px', height: '8px', background: '#10B981', borderRadius: '50%'}}></div> Online</div>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 0' }}>
               <div style={{ alignSelf: 'center', background: 'rgba(255,255,255,0.05)', padding: '8px 16px', borderRadius: '100px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                 Você encontrou um amistoso! Combinem os detalhes abaixo.
               </div>
               
               {chatMessages.map((msg, i) => (
                  <div key={i} style={{ 
                    alignSelf: msg.sender === 'me' ? 'flex-end' : 'flex-start',
                    background: msg.sender === 'me' ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)',
                    color: msg.sender === 'me' ? 'var(--text-dark)' : 'white',
                    padding: '12px 16px', borderRadius: '16px', maxWidth: '80%',
                    borderBottomRightRadius: msg.sender === 'me' ? '4px' : '16px',
                    borderBottomLeftRadius: msg.sender === 'opponent' ? '4px' : '16px'
                  }}>
                    {msg.text}
                    <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '4px', textAlign: msg.sender === 'me' ? 'right' : 'left' }}>
                      {msg.time.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </div>
                  </div>
               ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <input 
                type="text" 
                value={chatMessage}
                onChange={e => setChatMessage(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
                placeholder="Escreva uma mensagem..."
                style={{ flex: 1, padding: '16px', borderRadius: '100px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '16px' }}
              />
              <button 
                onClick={handleSendMessage}
                style={{ background: 'var(--primary-color)', border: 'none', width: '56px', height: '56px', borderRadius: '50%', color: 'var(--text-dark)', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}
              >
                 <ArrowLeft size={24} style={{ transform: 'rotate(180deg)' }} />
              </button>
            </div>
          </div>
        ) : isActive ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            {matchFound ? (
              <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
                <div style={{ 
                  width: '120px', height: '120px', background: 'rgba(58, 142, 88, 0.2)', 
                  borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                  margin: '0 auto 24px', border: '4px solid #3A8E58'
                }}>
                  <Trophy size={64} color="#3A8E58" />
                </div>
                <h2 style={{ fontSize: '32px', fontWeight: 800, color: '#3A8E58', marginBottom: '8px' }}>
                  MATCH ENCONTRADO!
                </h2>
                <div style={{ background: 'rgba(255,255,255,0.05)', padding: '24px', borderRadius: '16px', marginBottom: '32px', textAlign: 'left' }}>
                  <p style={{ margin: '0 0 8px 0', color: 'white', fontSize: '18px' }}><strong>Adversário:</strong> {matchFound.name}</p>
                  <p style={{ margin: '0 0 8px 0', color: 'var(--text-secondary)' }}><strong>Categoria:</strong> {matchFound.level}</p>
                  <p style={{ margin: '0 0 8px 0', color: 'var(--text-secondary)' }}><strong>Distância:</strong> {matchFound.distance}</p>
                  <p style={{ margin: '0', color: 'var(--text-secondary)' }}><strong>Quadra:</strong> {matchFound.court}</p>
                </div>
                
                <div style={{ display: 'flex', gap: '16px' }}>
                  <button 
                    onClick={() => setIsChatOpen(true)}
                    className="button-primary"
                    style={{ flex: 1, padding: '16px', fontSize: '16px' }}
                  >
                    Iniciar Chat
                  </button>
                  <button 
                    onClick={handleDeactivateRadar}
                    style={{ 
                      background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '16px', 
                      borderRadius: '100px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', flex: 1 
                    }}
                  >
                    Descartar
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ 
                  width: '100px', height: '100px', background: 'rgba(58, 142, 88, 0.1)', 
                  borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                  margin: '0 auto 24px', position: 'relative' 
                }}>
                  <div style={{
                    position: 'absolute', inset: 0, border: '2px solid #3A8E58', borderRadius: '50%',
                    animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite'
                  }}></div>
                  <Radio size={48} color="#3A8E58" />
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Você está ativo no Radar!
                </h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                  Buscando adversários num raio de {radius}km...
                </p>
                
                <button 
                  onClick={handleDeactivateRadar}
                  style={{ 
                    background: '#EF4444', color: 'white', border: 'none', padding: '16px 32px', 
                    borderRadius: '100px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', width: '100%' 
                  }}
                >
                  Parar Busca
                </button>
              </div>
            )}
            <style>{`
              @keyframes ping {
                75%, 100% {
                  transform: scale(2);
                  opacity: 0;
                }
              }
              @keyframes fadeIn {
                from { opacity: 0; transform: scale(0.9); }
                to { opacity: 1; transform: scale(1); }
              }
            `}</style>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            
            {/* Raio de Busca */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>
                <MapPin size={20} color="var(--primary-color)" /> Raio de Busca
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <input 
                  type="range" 
                  min="5" max="50" step="5" 
                  value={radius} 
                  onChange={e => setRadius(Number(e.target.value))}
                  style={{ flex: 1, accentColor: 'var(--primary-color)' }} 
                />
                <span style={{ fontSize: '18px', fontWeight: 800, color: 'var(--primary-color)', minWidth: '60px' }}>
                  {radius} km
                </span>
              </div>
            </div>

            {/* Minha Categoria */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>
                <Trophy size={20} color="var(--primary-color)" /> Minha Categoria
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {['Especial', 'A', 'B', 'C'].map(cat => (
                  <button 
                    key={cat}
                    onClick={() => setMyCategory(cat)}
                    style={{ 
                      flex: 1, minWidth: '100px', padding: '12px', borderRadius: '12px', border: '2px solid',
                      borderColor: myCategory === cat ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)',
                      background: myCategory === cat ? 'var(--primary-color)' : 'rgba(255,255,255,0.05)',
                      color: myCategory === cat ? 'white' : 'var(--text-secondary)',
                      fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s'
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Categoria do Adversário */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  <Target size={20} color="var(--primary-color)" /> Categoria do Adversário
                </label>
                <button 
                  onClick={handleToggleAllOpponents}
                  style={{ background: 'none', border: 'none', color: '#60A5FA', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
                >
                  {selectedOpponentCategories.length === opponentCategories.length ? 'Desmarcar Todas' : 'Marcar Todas'}
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {opponentCategories.map(cat => (
                  <button 
                    key={cat}
                    onClick={() => toggleOpponentCategory(cat)}
                    style={{ 
                      flex: 1, minWidth: '100px', padding: '12px', borderRadius: '12px', border: '2px solid',
                      borderColor: selectedOpponentCategories.includes(cat) ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)',
                      background: selectedOpponentCategories.includes(cat) ? 'rgba(58, 142, 88, 0.1)' : 'rgba(255,255,255,0.05)',
                      color: selectedOpponentCategories.includes(cat) ? 'var(--primary-color)' : 'var(--text-secondary)',
                      fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s'
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Quadra */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>
                <MapPin size={20} color="var(--primary-color)" /> Quadra
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                {['Possui Quadra', 'Quadra Alugada'].map(type => (
                  <button 
                    key={type}
                    onClick={() => setCourtType(type as any)}
                    style={{ 
                      flex: 1, padding: '12px', borderRadius: '12px', border: '2px solid',
                      borderColor: courtType === type ? '#60A5FA' : 'rgba(255,255,255,0.1)',
                      background: courtType === type ? 'rgba(96, 165, 250, 0.1)' : 'rgba(255,255,255,0.05)',
                      color: courtType === type ? '#60A5FA' : 'var(--text-secondary)',
                      fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s'
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Minha Idade */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>
                <Calendar size={20} color="var(--primary-color)" /> Minha Idade
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {ageOptions.map(age => (
                  <button 
                    key={age}
                    onClick={() => setMyAge(myAge === age ? '' : age)}
                    style={{ 
                      flex: 1, minWidth: '130px', padding: '12px', borderRadius: '12px', border: '2px solid',
                      borderColor: myAge === age ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)',
                      background: myAge === age ? 'var(--primary-color)' : 'rgba(255,255,255,0.05)',
                      color: myAge === age ? 'white' : 'var(--text-secondary)',
                      fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', fontSize: '14px'
                    }}
                  >
                    {age}
                  </button>
                ))}
              </div>
            </div>

            {/* Faixa Etária do Adversário (Opcional) */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  <Calendar size={20} color="var(--primary-color)" /> Faixa Etária do Adversário <span style={{fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 400}}>(Opcional)</span>
                </label>
                <button 
                  onClick={handleToggleAllAges}
                  style={{ background: 'none', border: 'none', color: '#60A5FA', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
                >
                  {selectedAges.length === ageOptions.length ? 'Desmarcar Todas' : 'Marcar Todas'}
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {ageOptions.map(age => (
                  <button 
                    key={age}
                    onClick={() => toggleAge(age)}
                    style={{ 
                      flex: 1, minWidth: '130px', padding: '12px', borderRadius: '12px', border: '2px solid',
                      borderColor: selectedAges.includes(age) ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)',
                      background: selectedAges.includes(age) ? 'rgba(58, 142, 88, 0.1)' : 'rgba(255,255,255,0.05)',
                      color: selectedAges.includes(age) ? 'var(--primary-color)' : 'var(--text-secondary)',
                      fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', fontSize: '14px'
                    }}
                  >
                    {age}
                  </button>
                ))}
              </div>
            </div>

            <button 
              onClick={handleActivateRadar}
              className="button-primary"
              style={{ width: '100%', padding: '18px', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '16px' }}
            >
              <Radio size={24} /> Ficar Ativo no Radar de Busca
            </button>

          </div>
        )}

        {!isChatOpen && previousChats.length > 0 && (
           <div style={{ marginTop: '32px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '24px' }}>
              <h3 style={{ color: 'white', fontSize: '18px', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                 Conversas Anteriores
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                 {previousChats.map(chat => (
                    <div key={chat.matchId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', padding: '14px 20px', borderRadius: '16px', transition: 'all 0.2s' }}>
                       <div onClick={() => {
                         setMatchFound({ userId: chat.opponentId, name: chat.opponentName });
                         setIsChatOpen(true);
                       }} style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, cursor: 'pointer' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(58, 142, 88, 0.1)', color: '#3A8E58', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 800, fontSize: '16px' }}>
                             {chat.opponentName.substring(0,2).toUpperCase()}
                          </div>
                          <div>
                             <div style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>{chat.opponentName}</div>
                             <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '250px' }}>
                                {chat.lastMessage || 'Nenhuma mensagem'}
                             </div>
                          </div>
                       </div>
                       <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {chat.lastMessageTime && (
                             <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px' }}>
                                {new Date(chat.lastMessageTime).toLocaleDateString([], {day: '2-digit', month: '2-digit'})}
                             </div>
                          )}
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm('Deseja realmente excluir esta conversa?')) {
                              try {
                                const res = await fetch(`${API_URL}/api/friendly-match/chat/${chat.matchId}`, {
                                  method: 'DELETE',
                                  headers: getAuthHeader()
                                });
                                if (res.ok) fetchPreviousChats();
                              } catch (e) { console.error(e); }
                            }
                          }} style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#EF4444', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                            Excluir
                          </button>
                       </div>
                    </div>
                 ))}
              </div>
           </div>
        )}
      </div>
    </div>
  );
};
